import { Pause } from '@/components/icons/pauseIcon';
import { useTaskTimerStore } from '@/entities/task/model/stores/task-timer';
import { completeTask } from '@/entities/task/api/tasks';
import { invalidateTaskCaches } from '@/features/tasks/model/useTaskActions';
import { useIsMobile } from '@/hooks/ui/useMediaQuery';
import { useUsers, useUserUtils } from '@/hooks/users/useUserActions';
import { UserAvatar } from '@/pages/groups/UserAvatar';
import { cn } from '@/shared/lib/cn';
import type { Task } from '@/shared/types/task';
import { Button } from '@/shared/ui/Button';
import { formatHM } from '@/shared/utils/helpers';
import { useQueryClient, type Query } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Flag, PlayIcon, User } from 'lucide-react';
import { type CSSProperties, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { taskDetailPath } from '@/entities/task/model/task-route';

type MeetingCardVariant = 'dark' | 'light';

type MeetingCardProps = {
  task: Task;
  className?: string;
  propsStyles?: CSSProperties;
  stopButtonClassName?: string;
  variant?: MeetingCardVariant;
};

const variantStyles: Record<
  MeetingCardVariant,
  {
    card: string;
    time: string;
    title: string;
    responsible: string;
    avatarFallback: string;
    playButton: string;
    pauseColor: string;
    finishButton: string;
  }
> = {
  dark: {
    card: "relative flex cursor-pointer flex-col rounded-md border border-[#8AE6FF80] py-2 pl-5 pr-4 font-semibold text-white shadow-md after:absolute after:bottom-2 after:left-1.5 after:top-2 after:w-px after:bg-white after:content-['']",
    time: '',
    title: 'mt-2 pb-3 text-xs',
    responsible: 'whitespace-nowrap text-sm font-normal',
    avatarFallback: 'flex size-full items-center justify-center bg-gray-500',
    playButton: '',
    pauseColor: 'white',
    finishButton: 'rounded-full text-xs',
  },
  light: {
    card: "relative flex min-h-[7.5rem] cursor-pointer flex-col rounded-xl border border-primary/25 bg-card py-3 pl-5 pr-4 shadow-sm transition-[box-shadow,border-color] hover:border-primary/40 hover:shadow-md after:absolute after:bottom-3 after:left-2 after:top-3 after:w-1 after:rounded-full after:bg-primary after:content-['']",
    time: 'text-sm font-semibold text-primary',
    title: 'mt-1.5 line-clamp-2 pb-2 text-xs font-normal text-muted-foreground',
    responsible:
      'max-w-[45%] truncate text-xs font-normal text-muted-foreground',
    avatarFallback:
      'flex size-full items-center justify-center bg-muted text-muted-foreground',
    playButton: 'text-primary hover:bg-primary/10',
    pauseColor: 'var(--primary)',
    finishButton:
      'rounded-full border-primary text-xs text-primary hover:bg-primary/10',
  },
};

type CacheItem = { id?: string | number };

function removeTaskFromCache(value: unknown, taskId: string): unknown {
  if (Array.isArray(value)) {
    return value.filter(
      (task) =>
        String((task as CacheItem | null | undefined)?.id ?? '') !== taskId
    );
  }

  if (typeof value !== 'object' || value === null || !('items' in value)) {
    return value;
  }

  const cached = value as Record<string, unknown>;

  if (!Array.isArray(cached.items)) {
    return value;
  }

  return {
    ...cached,
    items: cached.items.filter((task) => {
      const id = (task as CacheItem | null | undefined)?.id ?? '';
      return String(id) !== taskId;
    }),
  };
}

function isTasksQuery(query: Query) {
  const key = Array.isArray(query.queryKey) ? query.queryKey[0] : undefined;
  return typeof key === 'string' && key.includes('tasks');
}

/**
 * У встреч со смарт-процессом в конце названия идёт «{этап} {компания}»
 * (эти данные приходят отдельно в task.project) — в заголовке показываем
 * только путь до этого хвоста.
 */
function getMeetingTitleHead(rawTitle: string) {
  const parts = rawTitle
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts.slice(0, -1).join(' / ') : rawTitle.trim();
}

export function MeetingCardHeader({
  task,
  className,
  propsStyles,
  stopButtonClassName,
  variant = 'dark',
}: MeetingCardProps) {
  const styles = variantStyles[variant];
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [isHidden, setIsHidden] = useState(false);

  const { activeTaskId, tasks, startTask, requestPause } = useTaskTimerStore();

  const responsibleId = task.assigneeId ?? null;
  const { data: responsibleUser } = useUsers.useById(responsibleId);
  const responsibleDisplayName = useUserUtils.getDisplayName(responsibleUser);

  const timerTask = tasks.find((item) => item.taskId === task.id);
  const isActive = activeTaskId === task.id;
  const isRunning = timerTask?.isRunning ?? false;
  const startTime = task.dueDate ? format(parseISO(task.dueDate), 'HH:mm') : '';
  const duration = task.timeEstimate ? formatHM(task.timeEstimate) : '';

  // Данные встречи приходят с бэка: project.{companyTitle, StageName}
  // (project может быть null) + StartDatePlan/EndDatePlan.
  const meetingProject = task.project as ANY;
  const companyName = meetingProject?.companyTitle ?? null;
  const meetingAction = meetingProject?.StageName ?? null;

  // Время: старт — startDatePlan, конец — endDatePlan, а если его нет — дедлайн.
  const planStart = task.startDatePlan
    ? format(parseISO(task.startDatePlan), 'HH:mm')
    : null;
  const planEnd = task.endDatePlan
    ? format(parseISO(task.endDatePlan), 'HH:mm')
    : task.dueDate
      ? format(parseISO(task.dueDate), 'HH:mm')
      : null;
  const timeRange = planStart
    ? planEnd
      ? `${planStart} - ${planEnd}`
      : planStart
    : (planEnd ?? '');

  // В заголовке убираем хвост «{этап} {компания}» — показываем их отдельно.
  const rawTitle = task.title ?? '';
  const meetingTitle =
    companyName || meetingAction ? getMeetingTitleHead(rawTitle) : rawTitle;

  if (isHidden) {
    return null;
  }

  const handleOpenTask = () => {
    if (isMobile) {
      window.scrollTo({ top: 0, left: 0 });
    }

    navigate(taskDetailPath(task.id));
  };

  const handleStartPause = () => {
    if (isActive && isRunning) {
      requestPause(task.id);
      return;
    }

    startTask(task.id);
  };

  const handleFinishTask = async () => {
    const taskId = String(task.id);

    try {
      await queryClient.cancelQueries({ predicate: isTasksQuery });
      await completeTask(taskId);

      setIsHidden(true);
      queryClient.setQueriesData({ predicate: isTasksQuery }, (old) =>
        removeTaskFromCache(old, taskId)
      );
      // Полноценно перечитываем списки/доски/деталь с сервера — оптимистичное
      // удаление выше покрывает не все формы кэша (например, канбан-доску).
      await invalidateTaskCaches(queryClient);

      toast.success('Задача завершена');
    } catch (error) {
      console.error('MeetingCardHeader handleFinishTask error', error);
      toast.error('Не удалось завершить задачу');
    }
  };

  if (variant === 'light') {
    return (
      <div
        onClick={handleOpenTask}
        style={propsStyles}
        className={cn(
          'flex cursor-pointer flex-col gap-[26px] rounded-[8.49px] bg-white p-[12.73px] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)] ring-1 ring-black/[0.04] transition-shadow hover:shadow-md',
          className
        )}
      >
        <span className="line-clamp-2 text-[16px] font-medium leading-[19px] tracking-[-0.03em] text-black">
          {meetingTitle}
        </span>

        <div className="mt-auto flex flex-col gap-[16px]">
          {(companyName || meetingAction) && (
            <div className="flex min-w-0 items-center gap-[24px]">
              {companyName && (
                <span className="truncate text-[14px] font-medium leading-[17px] tracking-[-0.03em] text-[#8A8A8A]">
                  {companyName}
                </span>
              )}
              {meetingAction && (
                <span className="shrink-0 text-[14px] font-medium leading-[17px] tracking-[-0.03em] text-[#8A8A8A]">
                  {meetingAction}
                </span>
              )}
            </div>
          )}

          <div className="flex items-end justify-between gap-2">
            <div className="flex min-w-0 items-center gap-[6px]">
              {responsibleUser ? (
                <UserAvatar user={responsibleUser} size="xs" />
              ) : (
                <div className="flex size-[14.67px] items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <User className="size-3" />
                </div>
              )}
              <span className="truncate text-[12px] font-medium leading-[15px] tracking-[-0.03em] text-[#575757]">
                {responsibleDisplayName}
              </span>
            </div>

            <div className="flex shrink-0 flex-col items-end gap-[10px]">
              {timeRange && (
                <div className="flex items-center gap-[4px]">
                  <Flag
                    className="size-[12px] shrink-0 text-[#57B792]"
                    strokeWidth={1.8}
                  />
                  <span className="whitespace-nowrap text-[16px] font-semibold leading-[19px] tracking-[-0.03em] text-[#57B792]">
                    {timeRange}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-[8px]">
                <button
                  type="button"
                  aria-label={isRunning ? 'Пауза' : 'Старт'}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleStartPause();
                  }}
                  className="flex size-7 items-center justify-center rounded-full text-primary transition hover:bg-primary/10"
                >
                  {isRunning ? (
                    <Pause color="var(--primary)" />
                  ) : (
                    <PlayIcon className="size-4" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleFinishTask();
                  }}
                  className="rounded-[10px] border border-[#142141]/25 px-[12px] py-[6px] text-[12px] font-medium leading-[16px] tracking-[-0.5px] text-[#142141] transition hover:bg-[#142141]/[0.06]"
                >
                  Завершить
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={handleOpenTask}
      style={propsStyles}
      className={cn(styles.card, className)}
    >
      <div className="absolute right-3 top-3 flex items-center gap-2">
        <div className="size-6 shrink-0 overflow-hidden rounded-full">
          {responsibleUser ? (
            <UserAvatar user={responsibleUser} size="sm" />
          ) : (
            <div className={styles.avatarFallback}>
              <User className="size-4" />
            </div>
          )}
        </div>
        <span className={styles.responsible}>{responsibleDisplayName}</span>
      </div>

      <span className={styles.time}>
        {startTime}
        {duration ? ` - ${duration}` : null}
      </span>

      <span className={styles.title}>{task.title}</span>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={styles.playButton}
          onClick={(event) => {
            event.stopPropagation();
            handleStartPause();
          }}
        >
          {isRunning ? <Pause color={styles.pauseColor} /> : <PlayIcon />}
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(styles.finishButton, stopButtonClassName)}
          onClick={(event) => {
            event.stopPropagation();
            void handleFinishTask();
          }}
        >
          Завершить
        </Button>
      </div>
    </div>
  );
}
